import { cn } from '@/lib/utils';

/**
 * The Bramble mark: the blackberry from the app icon — seven drupelets, a stem and one
 * leaf.
 *
 * Redrawn from `marketing/icon-light.svg` / `marketing/icon-dark.svg`, the same artwork
 * that ships as the iOS app icon, so the site and the home-screen icon are recognisably
 * the same thing. Keep all four in step: these two files, this component, and
 * `public/favicon.svg`.
 *
 * Colours are literal rather than `currentColor` because this is a berry, not a glyph —
 * the app icon is pink-on-cream and green, and a mark that turned grey inside a muted
 * paragraph would stop reading as the icon. Dark mode is a Tailwind `dark:` variant, not a
 * `prefers-color-scheme` query, because the site's theme is class-based and someone who
 * toggles it by hand must get the matching artwork. (`favicon.svg` has no class to read,
 * so it uses the media query instead — the one place the two differ.)
 *
 * No background plate: the icon's cream/near-black field is the phone's app tile. Inline
 * on a page the fruit sits directly on whatever is behind it.
 */
export function BrambleMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 1024 1024"
      fill="none"
      aria-hidden="true"
      className={cn('size-7', className)}
      role="presentation"
    >
      {/* Stem and one leaf, drawn behind the fruit. */}
      <path
        d="M512 560 V286"
        className="stroke-[#692151] dark:stroke-[#C7A2BA]"
        strokeWidth="46"
        strokeLinecap="round"
      />
      <path
        d="M502 300 C412 296 350 254 340 192 C424 172 492 216 502 300 Z"
        className="fill-[#3B684A] dark:fill-[#509567]"
      />
      {/* Seven drupelets packed into one mass, alternating tones for depth. */}
      <circle cx="599" cy="408" r="100" className="fill-[#B03B79] dark:fill-[#DE5CA0]" />
      <circle cx="425" cy="408" r="100" className="fill-[#9A2A66] dark:fill-[#BC3A80]" />
      <circle cx="687" cy="560" r="100" className="fill-[#B03B79] dark:fill-[#DE5CA0]" />
      <circle cx="337" cy="560" r="100" className="fill-[#9A2A66] dark:fill-[#BC3A80]" />
      <circle cx="599" cy="712" r="100" className="fill-[#9A2A66] dark:fill-[#BC3A80]" />
      <circle cx="425" cy="712" r="100" className="fill-[#B03B79] dark:fill-[#DE5CA0]" />
      <circle cx="512" cy="560" r="100" className="fill-[#A6306F] dark:fill-[#D14791]" />
    </svg>
  );
}
